/**
 * Collapsing session state into something an operator can read at a glance.
 *
 * Shared by the API (which stamps `liveState` onto every row) and the web
 * client (which re-derives it locally as the clock advances, so a row can go
 * `live → stale` without waiting for a server push).
 */

import {
  STEADY_STATE_STEPS,
  STEP_INDEX,
  type LiveState,
  type PipelineStep,
} from './domain.js'
import type { RunPhase } from './dto.js'

/* ------------------------------------------------------------------ *
 * This system's runs, and ONLY this system's runs
 *
 * There used to be a second derivation here, `deriveLiveState`, which read
 * `customers.sessionStatus` / `currentStep` / `lastHeartbeatAt`. Those fields
 * are written by a SIBLING fleet on the shared cluster; this process has never
 * written one and now does not read one either.
 *
 * Blending the two was the bug. `resolveLiveState` preferred this system's run
 * for ten minutes and then handed the row back to the sibling, so a customer
 * could read "live" — with a Stop button — for a session this console never
 * started and cannot stop. Pressing Stop returned `{wasRunning: false}` while
 * the sibling carried on beating. The opposite also happened: a run of ours
 * whose entry had just been swept read `never_started` while five workers were
 * polling for it.
 *
 * A row now says exactly what THIS system is doing, or `never_started`. That is
 * a narrower claim and it is one we can actually stand behind.
 * ------------------------------------------------------------------ */

/**
 * How long a FINISHED run keeps its row before it reads as never-started.
 *
 * Matches `RETAIN_FINISHED_MS` in the run registry, which is what actually
 * evicts the entry — so the window is really "as long as there is anything to
 * report", and this constant is what keeps the browser's live re-derivation
 * agreeing with the server about when that stops being true.
 */
export const RUN_OWNS_ROW_MS = 10 * 60_000

/** Run phases where work is in progress, so the run is the whole truth. */
export const RUN_ACTIVE_PHASES: readonly RunPhase[] = Object.freeze([
  'starting',
  'signing_in',
  'awaiting_otp',
  'applying',
  'holding',
])

const ACTIVE_PHASES = new Set<RunPhase>(RUN_ACTIVE_PHASES)

/**
 * Phases that claim nothing is happening.
 *
 * `live` (below) is only allowed to override these. An active phase is already
 * the more specific answer — `awaiting_otp` says more than "running" — so the
 * machinery flag must not flatten it.
 */
const DORMANT_PHASES = new Set<RunPhase>(['idle', 'failed', 'stopped'])

/**
 * The phase to SHOW, with the machinery flag applied.
 *
 * `phase` records what was last REPORTED about a run; `live` records whether the
 * API still has a hunt, a heartbeat, a watch or the wizard running for this
 * customer. When the two disagree the machinery is the fact, and the customer
 * ROW has worked that way since it was written (`runOwnsLiveState` below, and
 * the same expression in the table). The drawer did not, and that asymmetry is
 * what let it tell an operator a customer had FAILED while the schedule watch
 * underneath it logged 1,757 more checks — and then offer a "Run again" button
 * that the still-live watch answered with 409 "already running".
 *
 * Only DORMANT phases are overridden. An active phase is already the more
 * specific answer — `awaiting_otp` says more than "something is running" — so
 * the flag must never flatten one.
 *
 * `holding` is reused rather than growing `RunPhase` with a new member: a new
 * variant would ripple through the shared union, the live-state map and the row
 * pill for a distinction none of them has room to draw.
 */
export function displayRunPhase(run: RunLiveHint | null | undefined): RunPhase {
  if (!run) return 'idle'
  return run.live === true && DORMANT_PHASES.has(run.phase) ? 'holding' : run.phase
}

/**
 * A run phase in the vocabulary the Session column and its filters speak.
 *
 * The four setup phases all collapse to `starting` because that is exactly what
 * the legacy value means — running, not yet in steady state. `holding` is
 * `live`: the seat is claimed and the heartbeat is going, which is the same
 * condition `deriveLiveState` calls live.
 *
 * `idle` is deliberately null. An entry exists for a customer the moment
 * anything is noted about them, including before a run begins, and a registry
 * entry is not a session.
 */
const RUN_PHASE_LIVE_STATE: Readonly<Record<RunPhase, LiveState | null>> = Object.freeze({
  idle: null,
  starting: 'starting',
  signing_in: 'starting',
  awaiting_otp: 'starting',
  applying: 'starting',
  holding: 'live',
  failed: 'failed',
  stopped: 'stopped',
})

/** The fields of a run that decide a row's state. */
export interface RunLiveHint {
  phase: RunPhase
  /** Epoch ms of the last change — the clock the retention window runs on. */
  updatedAt: number
  /**
   * Whether the API process still has MACHINERY running for this customer — a
   * hunt, a heartbeat or the onboarding wizard — irrespective of what `phase`
   * says.
   *
   * The two can disagree, and when they do this one is the fact. A run entry is
   * a record of what was reported; the machinery is what is actually making
   * requests. Three ways they come apart, all observed:
   *
   *   - A sign-in fails and stamps `failed`, while the hunt started by the
   *     PREVIOUS successful run is still polling.
   *   - A terminal entry is swept after ten minutes and rebuilt blank by the
   *     next log line, so a customer at 125 minutes reads `idle` at `0s`.
   *   - A renewal ends a run and the wizard carries on.
   *
   * In every case the console offered Start (refused with "already running")
   * and hid Stop, which is how an operator ends up with no way to stop five
   * workers. Carrying the flag on the hint is what lets one rule fix all three.
   */
  live?: boolean
}

/** Whether this system's run decides the row, rather than the legacy fields. */
export function runOwnsLiveState(run: RunLiveHint | null | undefined, now = Date.now()): boolean {
  if (!run) return false
  // Machinery outranks the clock and the phase both: a swept-and-rebuilt entry
  // has a fresh `updatedAt` but reads `idle`, and a failed one is outside the
  // retention window within ten minutes while its hunt still runs.
  if (run.live === true) return true
  if (run.phase === 'idle') return false
  if (ACTIVE_PHASES.has(run.phase)) return true
  return now - run.updatedAt < RUN_OWNS_ROW_MS
}

/**
 * The state this run imposes on its row, or null when it imposes none.
 *
 * Separate from `resolveLiveState` because the query planner needs the answer
 * *before* it has a document to read a legacy state from: it has to know which
 * customers a `liveState` filter must reach for, and those are exactly the ones
 * this returns a non-null value for.
 */
export function runLiveState(
  run: RunLiveHint | null | undefined,
  now = Date.now()
): LiveState | null {
  if (!runOwnsLiveState(run, now)) return null
  const hint = run as RunLiveHint
  /*
   * Machinery answers for the phases that claim nothing is happening.
   *
   * Only those: an active phase is the more specific truth and keeps it. This is
   * what makes Stop reachable — `failed` and `idle` are in neither `CAN_STOP`
   * nor the run-active set, so a customer whose entry read either of them while
   * five workers polled had no control that could reach them.
   */
  if (hint.live === true && DORMANT_PHASES.has(hint.phase)) return 'live'
  return RUN_PHASE_LIVE_STATE[hint.phase]
}

/**
 * The one answer the pill, the filter and the counters all read.
 *
 * `never_started` when no run owns the row, and that is now a complete answer
 * rather than a fallback: this system has no run for that customer, and it has
 * no opinion about whether anything else does.
 *
 * Took a `legacy: LiveState` first argument until the sibling's session fields
 * stopped being read. Renamed rather than left with a shrinking signature, so
 * the compiler names every caller that needs rethinking instead of silently
 * accepting one fewer argument.
 */
export function liveStateOf(run: RunLiveHint | null | undefined, now = Date.now()): LiveState {
  return runLiveState(run, now) ?? 'never_started'
}

/** Position in the pipeline, or -1 when the step is unrecognised. */
export function stepIndexOf(step: string | null | undefined): number {
  if (!step) return -1
  return STEP_INDEX[step] ?? -1
}

/** 0–1 progress. Steady-state steps report 1 — they are a destination. */
export function stepProgress(step: string | null | undefined): number {
  const i = stepIndexOf(step)
  if (i < 0) return 0
  if ((STEADY_STATE_STEPS as readonly string[]).includes(step as PipelineStep)) return 1
  const target = STEP_INDEX['keep_alive_ping'] ?? 15
  return Math.min(1, i / target)
}

/** Display metadata. Kept next to the derivation so they cannot drift apart. */
export const LIVE_STATE_META: Readonly<
  Record<LiveState, { label: string; tone: 'live' | 'ok' | 'warn' | 'danger' | 'neutral'; help: string }>
> = Object.freeze({
  never_started: {
    label: 'Not started',
    tone: 'neutral',
    help: 'No session has ever been created for this customer.',
  },
  starting: {
    label: 'Starting',
    tone: 'ok',
    help: 'Session is working through login, OTP and application setup.',
  },
  live: {
    label: 'Live',
    tone: 'live',
    help: 'Session is authenticated and sending keep-alive pings.',
  },
  stale: {
    label: 'Stale',
    tone: 'warn',
    help: 'Marked running but the heartbeat stopped — the worker is likely gone.',
  },
  failed: {
    label: 'Failed',
    tone: 'danger',
    help: 'Session ended with an error. See the log for the failing step.',
  },
  stopped: {
    label: 'Stopped',
    tone: 'neutral',
    help: 'Session was stopped or expired.',
  },
})
